import React, { useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import Spinner from 'components/loader/Spinner';
import useDatasetList from './hooks/useDatasetList';
import { Transition } from '@headlessui/react';
import Header, { ButtonConfig } from 'components/header/Header';
import { useModal } from 'hooks/useModal';
import { getPageNumber } from 'components/pager/getPageDetails';
import AutocompleteSearchBar from 'components/search/AutocompleteSearchBar';
import {
    DatasetListItem,
    StatusProps,
    UserRoleForDataset,
} from 'types/Dataset';
import { SearchFilter } from 'types/Filters';
import NoDatasetStatus from './components/NoDatasetStatus';
import DatasetCard from './DatasetCard';
import { NIcon, NLink } from '@innersource-nn/novoui-react';
import SearchQuestionIcon from 'assets/icons/SearchQuestionIcon';

interface DatasetDisplay {
    datasets: DatasetListItem[];
    title: string;
    status: {
        color: string;
        icon: React.JSX.Element;
    };
}
const props = { size: 18, className: 'fill-true-blue' };
const sharesFilter = {
    term: '',
    page: 1,
    pageSize: 100,
    roles: ['Owner'],
};
const DatasetList: React.FC = () => {
    const location = useLocation();
    const initalTerm = new URLSearchParams(location.search).get('query') || '';
    const navigate = useNavigate();
    const { openNewDatasetModal } = useModal();
    const [incompleteOpen, setIncompleteOpen] = useState(false);

    const [filter, setFilter] = useState<SearchFilter>({
        page: getPageNumber(location.search),
        pageSize: 1000,
        term: initalTerm,
        roles: ['BusinessOwner', 'DataSteward', 'Creator', 'Admin', 'Shared'],
    });

    const { items, error, loading } = useDatasetList(filter, sharesFilter);
    const datasetsLabels = items.nodes.map((node: DatasetListItem) => ({
        label: node.label,
        type: node.__typename.toLowerCase(),
        uri: node.datasetUri,
    }));

    const incompleteDatasets = items.nodes.filter(
        (item) =>
            item.statistics.tables === 0 && item.statistics.locations === 0,
    );
    const incompleteDatasetsCreator = incompleteDatasets.filter(
        (item) => item.userRoleForDataset === UserRoleForDataset.Creator,
    );
    const incompleteDatasetsRest = incompleteDatasets.filter(
        (item) => item.userRoleForDataset !== UserRoleForDataset.Creator,
    );

    const remainingList = items.nodes.filter(
        (item) =>
            !(item.statistics.tables === 0 && item.statistics.locations === 0),
    );

    const creatorDatasets = remainingList.filter(
        (item) => item.userRoleForDataset === UserRoleForDataset.Creator,
    );

    const accessibleDatasets = remainingList.filter(
        (item) => item.userRoleForDataset !== UserRoleForDataset.Creator,
    );

    const buttonConfig: ButtonConfig[] = [
        {
            label: 'Create Dataset',
            onClick: () => openNewDatasetModal({}),
        },
    ];
    const incompleteStatus: StatusProps = {
        color: 'alert',
        icon: <NIcon iconName="visibilityOff" {...props} />,
        tooltip:
            'This dataset is hidden by default as it does not contain any content',
    };

    const searchDatasetsGlobally = () => {
        navigate(`/discover?query=${filter.term}`);
    };

    const incompleteDisplays = [
        {
            datasets: incompleteDatasetsCreator,
            title: 'My Datasets',
            status: incompleteStatus,
        },
        {
            datasets: incompleteDatasetsRest,
            title: 'Other datasets',
            status: incompleteStatus,
        },
    ];

    const datasetDisplays = [
        {
            datasets: creatorDatasets,
            title: 'My Datasets',
            status: {
                color: 'info',
                icon: <NIcon iconName="person" {...props} />,
                tooltip: 'You are the creator of this dataset.',
            },
        },
        {
            datasets: accessibleDatasets,
            title: 'Accessible datasets',
            status: {
                color: 'success',
                icon: <NIcon iconName="lockOpen" {...props} />,
                tooltip: 'You are and admin of this dataset',
            },
        },
    ];

    const datasetSection = (
        datasets: DatasetDisplay[],
        subSection: boolean,
    ) => {
        return datasets.map((datasetArray, id) => {
            return (
                datasetArray.datasets.length > 0 && (
                    <div key={`dataset-div-${id}`} className="mt-24">
                        <b
                            className={`text-true-blue ${subSection ? 'text-md' : 'text-lg'}`}
                        >
                            {datasetArray.title}
                            {!subSection &&
                                ` (${datasetArray.datasets.length})`}
                        </b>
                        <div className="dataset-grid gap-y-16 ">
                            {datasetArray.datasets.map((item, index) => (
                                <DatasetCard
                                    {...item}
                                    key={index}
                                    status={datasetArray.status}
                                />
                            ))}
                        </div>
                    </div>
                )
            );
        });
    };

    return (
        <div>
            <Header title="Datasets" buttonConfigs={buttonConfig} />
            <AutocompleteSearchBar
                onTermChange={(e: string) => {
                    setFilter({ ...filter, term: e });
                }}
                items={datasetsLabels}
                loading={loading}
                placeholder="Search among all datasets you have access to or have created..."
            />
            {loading ? (
                <Spinner />
            ) : (
                !error &&
                (items.nodes.length === 0 ? (
                    filter.term === '' ? (
                        <NoDatasetStatus />
                    ) : (
                        <div className="empty-place-holder items-center">
                            <div className="flex flex-col items-center">
                                <div className="round-div-icon-middle bg-light-blue-200 h-[5rem] w-[5rem] ">
                                    <SearchQuestionIcon />
                                </div>
                                <p className="text-true-blue w-1/2 text-center mt-8">
                                    Looks like we couldn't find any results
                                    based on your search amongs dataset you have
                                    access to.&nbsp;
                                    <NLink
                                        href=""
                                        label="Click here to search throught all datasets available on datahub."
                                        onClick={searchDatasetsGlobally}
                                    ></NLink>
                                </p>
                            </div>
                        </div>
                    )
                ) : (
                    <div>
                        {incompleteDatasets.length > 0 && (
                            <div>
                                <div
                                    onClick={() =>
                                        setIncompleteOpen(!incompleteOpen)
                                    }
                                    className={
                                        'section-title flex gap-8 font-bold cursor-pointer items-center'
                                    }
                                >
                                    Empty datasets
                                    {` (${incompleteDatasets.length})`}
                                    <NIcon
                                        iconName={
                                            incompleteOpen
                                                ? 'chevronUp'
                                                : 'chevronDown'
                                        }
                                        {...props}
                                    />
                                </div>
                                <Transition
                                    show={incompleteOpen}
                                    enter="transition-enter-leave"
                                    enterFrom="transition-0"
                                    enterTo="transition-100"
                                    leave="transition-enter-leave"
                                    leaveFrom="transition-100"
                                    leaveTo="transition-0"
                                >
                                    <div>
                                        {datasetSection(
                                            incompleteDisplays,
                                            true,
                                        )}
                                    </div>
                                </Transition>
                            </div>
                        )}
                        {datasetSection(datasetDisplays, false)}
                    </div>
                ))
            )}
        </div>
    );
};

export default DatasetList;
